import React, { useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Card } from "../components/ui";
import { ShieldCheck, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";


export default function PublicVerify() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const verify = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/verify-bundle", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-neutral-50 dark:from-neutral-950 dark:to-black" data-testid="public-verify-page">
      <div className="mx-auto max-w-3xl space-y-6 p-6 md:p-10">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-[var(--primary)]/10 p-2 text-[var(--primary)]"><ShieldCheck size={26} /></div>
            <h1 className="font-head text-3xl">Verify a GCA-FopFo Regulatory Bundle</h1>
          </div>
          <p className="text-sm opacity-70 max-w-2xl">
            Upload any regulatory bundle ZIP you've received. This page rehashes every file inside the ZIP with SHA-256 and compares it to the hashes recorded in MANIFEST.txt at export time. If a single byte was changed after the bundle left our servers, it will show up here.
          </p>
        </div>

        <Card className="space-y-3" data-testid="verify-card">
          <div className="text-sm font-medium">1. Choose the ZIP file</div>
          <input type="file" accept=".zip" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="verify-file"
            className="text-sm" />
          <div className="text-sm font-medium">2. Run verification</div>
          <Button onClick={verify} disabled={busy || !file} data-testid="verify-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Verify integrity
          </Button>

          {err && (
            <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="verify-error">{err}</div>
          )}

          {result && (
            <div className={`rounded-md border p-3 text-sm ${result.ok ? "border-[#24A148]/40 bg-[#24A148]/10" : "border-[#DA1E28]/40 bg-[#DA1E28]/10"}`} data-testid="verify-result">
              <div className={`flex items-center gap-2 font-medium text-base ${result.ok ? "text-[#24A148]" : "text-[#DA1E28]"}`}>
                {result.ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                {result.ok ? "Bundle is intact." : `Tampering detected in ${result.total_files - result.verified_files} of ${result.total_files} files.`}
              </div>
              <div className="mt-2 text-xs opacity-75">
                <div><b>Bundle ID:</b> <code>{result.bundle_id || "—"}</code></div>
                <div><b>Generated:</b> {result.generated_at || "—"}</div>
                <div><b>Files verified:</b> {result.verified_files} / {result.total_files}</div>
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {result.results.map((r, i) => (
                  <div key={i} className={`flex items-center gap-2 ${r.status === "ok" ? "opacity-70" : r.status === "tampered" ? "text-[#DA1E28] font-medium" : "text-[#F1C21B]"}`} data-testid={`verify-row-${i}`}>
                    <span className="min-w-[80px]">{r.status === "ok" ? "✓ ok" : r.status === "tampered" ? "✗ TAMPERED" : "? MISSING"}</span>
                    <code className="text-[10px] opacity-60">{(r.expected || "").slice(0, 16)}…</code>
                    <span className="truncate">{r.filename}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <div className="text-xs opacity-50">
          This tool never stores your uploaded file. Verification is done in-memory and discarded when the response is sent.
        </div>
      </div>
    </div>
  );
}
